import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';
import { useToast } from '../../../context/ToastContext';
import { useStartAnalysisDb } from '../../../services/module1Service';
import blobService from '../../../services/storage/blobService';
import { transformAnalysisResults } from '../../../utils/analysis';
import { sanitizeClientId } from '../../../utils/helpers';
import { workflowService } from '../../../services/api/workflowService';
import Module1Service, { DbAnalysisStatus } from '../../../services/module1Service';
import { snowflakeApi } from '../../../utils/api';

export interface Module1HandlersProps {
  workflowId?: string;
  clientId?: string;
  workflow?: any;
  workflowData?: any;
  uploadedFilePath: string | null;
  setUploadedFilePath: (path: string | null) => void;
  setHasUploadedFile: (hasFile: boolean) => void;
  isUsingDatabaseApproach: boolean;
  setIsUsingDatabaseApproach: (isUsing: boolean) => void;
  isProcessing: boolean;
  setIsProcessing: (isProcessing: boolean) => void;
  setError: (error: string | null) => void;
  setIsUploading: (isUploading: boolean) => void;
  setIsCreatingWorkflow: (isCreating: boolean) => void;
  setBusinessName: (name: string) => void;
  setAnalysisResults: (results: any) => void;
  setActiveModuleId: (moduleId: number | null) => void;
  activeModuleId: number | null;
  setIsStepperCollapsed: (collapsed: boolean) => void;
  businessName: string;
  triggerRefresh?: () => void;
}

const useHandlers = ({
  workflowId,
  clientId,
  workflow,
  workflowData,
  uploadedFilePath,
  setUploadedFilePath,
  setHasUploadedFile,
  isUsingDatabaseApproach,
  setIsUsingDatabaseApproach,
  isProcessing,
  setIsProcessing,
  setError,
  setIsUploading,
  setIsCreatingWorkflow,
  setBusinessName,
  setAnalysisResults,
  setActiveModuleId,
  activeModuleId,
  setIsStepperCollapsed,
  businessName,
  triggerRefresh
}: Module1HandlersProps) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { updateWorkflowData, workflowData: contextWorkflowData, serviceStatus } = useAppContext();
  const startAnalysisDbMutation = useStartAnalysisDb();
  
  // Polling reference
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCreateWorkflow = async () => {
    if (!businessName.trim()) {
      showToast('Please enter a business name', 'error');
      return;
    }

    try {
      setIsCreatingWorkflow(true);
      
      // Determine which client ID to use - prioritize the URL param
      const effectiveClientId = clientId || localStorage.getItem('selected_client_id');
      
      // Debug logging
      console.log('Creating workflow with client ID:', {
        urlClientId: clientId,
        localStorageClientId: localStorage.getItem('selected_client_id'),
        effectiveClientId: effectiveClientId
      });
      
      if (!effectiveClientId) {
        showToast('No client selected. Please go back to client selection.', 'error');
        navigate('/client-selection');
        return;
      }
      
      // Validate client ID format - only allow alphanumeric characters and hyphens
      if (!/^[a-zA-Z0-9-]+$/.test(effectiveClientId)) {
        console.error('Invalid client ID format:', effectiveClientId);
        showToast('Invalid client ID format. Client IDs can only contain letters, numbers, and hyphens.', 'error');
        return;
      }
      
      console.log('Creating client and workflow using database-first approach');
      
      try {
        // Step 1: Create or get client using the database client function
        const sanitizedClientCode = sanitizeClientId(businessName);
        console.log('Creating database client with code:', sanitizedClientCode);
        
        const client = await workflowService.createDbClient(
          businessName, // Name
          sanitizedClientCode, // Client Code
          true // isActive
        );
        
        console.log('Database client created successfully:', client);
        
        // Step 2: Create workflow using the database workflow function
        console.log('Creating database workflow for client ID:', client.client_id);
        
        // Note: Do not pass external_id as backend API doesn't support it
        const dbWorkflow = await workflowService.createDbWorkflow(
          client.client_id, // Use database client ID
          businessName, // Workflow name
          'Created from Module 1 UI' // Description
          // No external_id parameter (caused errors)
        );
        
        console.log('Database workflow created successfully:', dbWorkflow);
        
        // Step 3: Navigate using database IDs
        console.log('Navigating with database IDs -', 
          `client_id: ${client.client_id}, workflow_id: ${dbWorkflow.workflow_id}`);
        
        // Store database workflow ID in localStorage
        localStorage.setItem('db_workflow_id', dbWorkflow.workflow_id.toString());
        localStorage.setItem('use_database_first', 'true');
        
        // Navigate using the database IDs for both client and workflow
        navigate(`/module1/client/${client.client_id}/${dbWorkflow.workflow_id}`);
        showToast('Workflow created successfully', 'success');
        
        // Important: Return here to prevent falling through to the outer catch block
        return;
      } catch (error) {
        console.error('Database-first workflow creation failed:', error);
        
        // Log error with detailed diagnostic information
        const appInsights = (window as any).__appInsights;
        if (appInsights) {
          appInsights.trackException({
            exception: error instanceof Error ? error : new Error('Unknown workflow creation error'),
            properties: {
              clientId: effectiveClientId,
              businessName,
              operation: 'handleCreateWorkflow',
              timestamp: new Date().toISOString(),
              errorMessage: error instanceof Error ? error.message : String(error)
            }
          });
        }
        
        // Show error to user
        showToast('Failed to create workflow. Please try again or contact support.', 'error');
        
        // Important: Set creating workflow to false to unblock the UI
        setIsCreatingWorkflow(false);
        
        // Return to prevent falling back to standard approach which has its own issues
        return;
      }
    } catch (error) {
      console.error('Error in workflow creation process:', error);
      
      // Enhanced error logging
      if (error && (error as any).response) {
        const errorResponse = (error as any).response;
        console.error('Server returned status:', errorResponse.status);
        console.error('Error response data:', errorResponse.data);
        console.error('Request URL:', errorResponse.config?.url);
        console.error('Request params:', errorResponse.config?.params);
        console.error('Request data:', errorResponse.config?.data);
        
        // Log to App Insights
        const appInsights = (window as any).__appInsights;
        if (appInsights) {
          appInsights.trackException({
            exception: new Error(`Workflow creation failed: ${errorResponse.status}`),
            properties: {
              status: errorResponse.status,
              response: JSON.stringify(errorResponse.data),
              url: errorResponse.config?.url,
              params: JSON.stringify(errorResponse.config?.params),
              requestData: errorResponse.config?.data
            }
          });
        }
      }
      
      showToast('Failed to create workflow', 'error');
    } finally {
      setIsCreatingWorkflow(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    try {
      // Get the workflow ID from the current workflow
      if (!workflowId) {
        showToast('No active workflow. Please create a workflow first.', 'error');
        throw new Error('No active workflow ID');
      }
      
      // Determine which client ID to use - prioritize the URL param
      let effectiveClientId = clientId;
      
      // If no client ID in URL, try localStorage
      if (!effectiveClientId) {
        const selectedClient = localStorage.getItem('selected_client_id');
        const workflowClient = localStorage.getItem(`workflow_${workflowId}_client`);
        effectiveClientId = selectedClient || workflowClient || undefined;
      }
      
      // If still no client ID but workflow has business_name, sanitize it
      if (!effectiveClientId && workflow?.business_name) {
        effectiveClientId = sanitizeClientId(workflow.business_name);
      }
      
      // Fall back to default if still no client ID - should never happen with our new routing
      if (!effectiveClientId) {
        console.warn('No client ID found, falling back to default');
        effectiveClientId = 'default';
      }
      
      console.log('Uploading file with client ID:', effectiveClientId);
      
      const blobPath = await blobService.uploadFileDirectly(file, { 
        containerName: 'client-data',
        clientId: effectiveClientId,
        workflowId,
        dataSource: 'module1-workflow'
      });
      
      setUploadedFilePath(blobPath);
      setHasUploadedFile(true);
      showToast('File uploaded successfully', 'success');
      return blobPath;
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast('Failed to upload file', 'error');
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const handleDbAnalysisComplete = async (businessInfo: any) => { 
    // Check if this is a Snowflake workflow
    if (businessInfo?.keywordSource === 'snowflake' && businessInfo?.blobPath) {
      // For Snowflake workflow, we already have the blob path
      setUploadedFilePath(businessInfo.blobPath);
      setHasUploadedFile(true);
    }
    
    // Modified validation to check for either traditional upload or Snowflake workflow
    if (!uploadedFilePath && !(businessInfo?.keywordSource === 'snowflake' && businessInfo?.blobPath)) {
      showToast('Please upload a file containing keywords or load keywords from Snowflake', 'error');
      return;
    }

    try {
      setIsProcessing(true);
      setError(null);
      
      // Always get client ID from URL or localStorage - no complex resolution
      const effectiveClientId = clientId || localStorage.getItem('selected_client_id');
      
      if (!effectiveClientId) {
        showToast('No client ID found. Please select a client first', 'error');
        setIsProcessing(false);
        return;
      }
      
      console.log('Starting analysis with client ID:', effectiveClientId);
      
      // SIMPLIFIED APPROACH: Directly use workflow ID from URL or generate a new one
      let analysisWorkflowId = workflowId;
      
      // If workflowId is a UUID, check for cached mapping to database ID
      if (typeof workflowId === 'string' && workflowId.includes('-')) {
        const cachedDbId = localStorage.getItem(`external_id_${workflowId}`);
        if (cachedDbId && /^\d+$/.test(cachedDbId)) {
          analysisWorkflowId = cachedDbId; // Keep as string to avoid type issues
          console.log('Using cached database ID:', analysisWorkflowId);
        }
      }
      
      // STEP 1: Save business configuration to database AND blob storage
      if (businessInfo && businessInfo.businessName) {
        try {
          // Format additional context without keyword limit and custom prompt
          let formattedAdditionalContext = [
            businessInfo.description ? `Business Description: ${businessInfo.description}` : '',
            businessInfo.targetAudience ? `Target Audience: ${businessInfo.targetAudience}` : '',
            businessInfo.additionalContext ? `Additional Context: ${businessInfo.additionalContext}` : ''
          ].filter(Boolean).join('\n');
          
          // Prepare config data for blob storage (legacy format)
          const blobConfigData = {
            workflow_id: analysisWorkflowId || workflowId,
            business_name: businessInfo.businessName || '',
            industry: businessInfo.industry || 'General',
            business_confirmed: true,
            brand_info: {}, // Could be expanded later
            additional_context: formattedAdditionalContext || undefined,
            client_id: effectiveClientId
          };
          
          // Prepare config data for database (separate fields format)
          const dbConfigData = {
            business_name: businessInfo.businessName || '',
            industry: businessInfo.industry || 'General',
            description: businessInfo.description || '',
            target_audience: businessInfo.targetAudience || '',
            additional_context: businessInfo.additionalContext || ''
          };
          
          // Save to blob storage (legacy)
          try {
            await Module1Service.getInstance().createBusinessConfig(
              blobConfigData
            );
          } catch (blobError) {
            console.error('Failed to save business config to blob storage:', blobError);
          }
          
          // Save to database
          if (workflow?.workflow_id) {
            try {
              await snowflakeApi.put(
                `/api/v1/db/workflow/${workflow.workflow_id}/business-config`, 
                dbConfigData
              );
              showToast('Business information saved', 'success');
            } catch (dbError) {
              console.error('Failed to save business config to database:', dbError);
            }
          }
        } catch (configError) {
          console.error('Failed to save business configuration:', configError);
          showToast('Warning: Business configuration could not be saved, but analysis will continue', 'warning');
        }
      }
      
      // STEP 2: Start the analysis
      // Use the blob path from either traditional upload or Snowflake workflow
      const blobPathToUse = businessInfo?.keywordSource === 'snowflake' && businessInfo?.blobPath 
        ? businessInfo.blobPath 
        : uploadedFilePath;
        
      // Use any type to avoid TypeScript errors with the parameters
      const analysisParams: any = {
        workflowId: analysisWorkflowId,
        inputBlob: blobPathToUse.trim(),
        isDraft: true,
        clientId: effectiveClientId,
        keywordLimit: businessInfo?.keywordLimit,
        customPrompt: businessInfo?.customPrompt
      };
      
      console.log('[handleSubmit] Starting analysis with params:', analysisParams);
      
      // Start database-first analysis with minimal parameters
      const dbResponse = await startAnalysisDbMutation.mutateAsync(analysisParams);
      
      console.log('Database-first analysis response:', dbResponse);
      
      // Trigger refresh to update stepper status when analysis starts
      if (triggerRefresh) {
        triggerRefresh();
      }
      
      // Handle immediate results
      if (dbResponse.status === 'draft' || dbResponse.status === 'finalized') {
        if (dbResponse.results) {
          const transformedData = transformAnalysisResults(dbResponse);
          setAnalysisResults(transformedData);
          showToast('Analysis completed successfully', 'success');
          setIsProcessing(false);
          
          // Trigger refresh to update stepper status when analysis completes immediately
          if (triggerRefresh) {
            triggerRefresh();
          }
          
          return;
        }
      }
      
      // Handle module ID for polling
      if (dbResponse.module_id) {
        setActiveModuleId(dbResponse.module_id);
        
        // Simple polling with linear delay - prevents excessive CPU usage
        if (dbResponse.status === 'processing' || dbResponse.status === 'created') {
          let attempts = 0;
          const maxAttempts = 60; // Increased from 20 to allow for up to 10 minutes of polling
          const pollInterval = 10000; // Increased from 5 seconds to 10 seconds
          
          const pollForResults = async () => {
            attempts++;
            
            try {
              const statusResponse: DbAnalysisStatus = await Module1Service.getInstance().getAnalysisStatusDb(dbResponse.module_id);
              // Check for terminal statuses to stop polling
              if (statusResponse.status === 'draft' || statusResponse.status === 'finalized' || statusResponse.status === 'completed') {
                if (statusResponse.results) {
                  // Clear any scheduled polling
                  if (pollingTimeoutRef.current) {
                    clearTimeout(pollingTimeoutRef.current);
                    pollingTimeoutRef.current = null;
                  }
                  // BYPASS the transformation pipeline - use raw results directly
                  const rawResults = statusResponse.results || {};
                  // Use type assertion for the entire object
                  const minimallyProcessedResults: any = {
                    topics: Array.isArray(rawResults.topics) ? rawResults.topics : [],
                    missing_themes: Array.isArray(rawResults.missing_themes) ? rawResults.missing_themes : []
                  };
                  
                  setAnalysisResults(minimallyProcessedResults);
                  updateWorkflowData(
                    {
                      ...contextWorkflowData,
                      analysisResult: minimallyProcessedResults,
                    }
                  )
                  showToast('Analysis completed successfully', 'success');

                  setIsProcessing(false);
                  const { results, output_type } = statusResponse;
                  console.log({results, output_type});
                  
                  // Trigger refresh to update stepper status when analysis completes
                  if (triggerRefresh) {
                    triggerRefresh();
                  }
                  
                  // Exit polling loop
                  return;
                }
              } else if (statusResponse.status === 'failed') {
                // Clear any scheduled polling on failure
                if (pollingTimeoutRef.current) {
                  clearTimeout(pollingTimeoutRef.current);
                  pollingTimeoutRef.current = null;
                }
                throw new Error(statusResponse.error || 'Analysis failed');
              } else if (attempts < maxAttempts) {
                // Log progress for longer running analyses
                if (attempts % 6 === 0) { // Log every minute
                  const minutesElapsed = (attempts * pollInterval) / 60000;
                  console.log(`Analysis still processing after ${minutesElapsed} minute(s)...`);
                  showToast(`Analysis in progress (${minutesElapsed} minute${minutesElapsed > 1 ? 's' : ''} elapsed)...`, 'info');
                }
                // Continue polling with the next timeout
                pollingTimeoutRef.current = setTimeout(pollForResults, pollInterval);
              } else {
                // Clear any scheduled polling when max attempts reached
                if (pollingTimeoutRef.current) {
                  clearTimeout(pollingTimeoutRef.current);
                  pollingTimeoutRef.current = null;
                }
                throw new Error('Analysis timed out after 10 minutes. The analysis may still be running - please try loading the latest results in a few minutes.');
              }
            } catch (err) {
              // Clear any scheduled polling on error
              if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
              }
              console.error("Error during polling:", err);
              setError(err instanceof Error ? err.message : "Error checking analysis status");
              showToast("Analysis status check failed", "error");
              setIsProcessing(false);
            }
          };
          
          // Initial delay before first poll to reduce immediate load
          pollingTimeoutRef.current = setTimeout(pollForResults, pollInterval);
        }
      } else {
        throw new Error('No module ID in response. Analysis may have failed to start.');
      }
    } catch (err) {
      console.error("Error during analysis:", err);
      
      // Fallback to standard approach on error
      if (isUsingDatabaseApproach) {
        showToast('Database approach failed, trying standard method', 'warning');
        setIsUsingDatabaseApproach(false);
        try {
          return await handleDbAnalysisComplete(businessInfo);
        } catch (fallbackErr) {
          setError("All analysis attempts failed");
          showToast("Failed to analyze with both approaches", "error");
        }
      } else {
        setError(err instanceof Error ? err.message : "Failed to analyze");
      }
      
      setIsProcessing(false);
    }
  };

  const handleReprocess = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // Check if we should use database-first approach
      if (isUsingDatabaseApproach && activeModuleId) {
        try {
          // Get the database workflow ID as string first
          const dbWorkflowIdStr = localStorage.getItem('db_workflow_id') || '';
          
          if (!dbWorkflowIdStr) {
            throw new Error("No database workflow ID found for reprocessing");
          }
          
          // Parse as string to avoid type issues
          const dbWorkflowId = dbWorkflowIdStr;
          
          // For reprocessing in database-first mode, we need to restart analysis
          // with the forceReanalysis flag set to true
          if (uploadedFilePath) {
            // Use a more flexible approach to avoid TypeScript errors
            const requestParams: any = {
              workflowId: dbWorkflowId,
              inputBlob: uploadedFilePath,
              isDraft: false // Not a draft since we're reprocessing
            };
            
            // Add forceReanalysis as a custom parameter
            requestParams.forceReanalysis = true;
            
            const dbResponse = await startAnalysisDbMutation.mutateAsync(requestParams);
            
            console.log('Database-first reprocessing response:', dbResponse);
            
            if (dbResponse && dbResponse.module_id) {
              // Update the module ID for status polling
              setActiveModuleId(dbResponse.module_id);
              
              // Save the module ID to localStorage for retrieval if page is refreshed
              const dbWorkflowId = localStorage.getItem('db_workflow_id');
              if (dbWorkflowId) {
                localStorage.setItem(`workflow_${dbWorkflowId}_module_id`, dbResponse.module_id.toString());
              }
              
              showToast('Reprocessing started using database-first approach', 'info');
            } else {
              throw new Error("No module ID returned from database-first reprocessing");
            }
          } else {
            throw new Error("No file path available for reprocessing");
          }
        } catch (dbError) {
          console.error('Database-first reprocessing failed:', dbError);
          throw dbError; // Propagate the error to the outer catch block
        }
      } 
      // Standard approach fallback
      else {
        // Reprocess with existing workflow data
        const response = await fetch(`/api/v1/module1/reprocess/${workflowId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(typeof window !== 'undefined' && localStorage.getItem('authguard_access_token')
              ? { Authorization: `Bearer ${localStorage.getItem('authguard_access_token')}` }
              : {}),
          }
        });

        if (!response.ok) {
          throw new Error("Reprocessing failed");
        }

        const data = await response.json();
        const transformedData = transformAnalysisResults(data);
        setAnalysisResults(transformedData);
        
        showToast('Analysis reprocessed successfully', 'success');
        setIsProcessing(false);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error reprocessing analysis';
      setError(errorMessage);
      showToast(errorMessage, 'error');
      setIsProcessing(false);
    }
  };

  const handleDatabaseToggleChange = useCallback((newState: boolean) => {
    setIsUsingDatabaseApproach(newState);
    localStorage.setItem('use_database_first', newState ? 'true' : 'false');
    showToast(`Database-first approach ${newState ? 'enabled' : 'disabled'}`, 'info');
  }, [setIsUsingDatabaseApproach, showToast]);

  const handleStepperCollapseChange = useCallback((collapsed: boolean) => {
    setIsStepperCollapsed(collapsed);
  }, [setIsStepperCollapsed]);

  // Cleanup function for polling
  const cleanupPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  return {
    handleCreateWorkflow,
    handleFileUpload,
    handleDbAnalysisComplete,
    handleReprocess,
    handleDatabaseToggleChange,
    handleStepperCollapseChange,
    cleanupPolling,
    pollingTimeoutRef
  };
};

export default useHandlers;
